import { createContext, ReactNode, useEffect, useState } from "react";
import api from "../services/api";
import { usePluginspace } from "../hooks/usePluginspace";
import { useLocation, useParams } from "react-router-dom";
import { useAppConfig } from "../hooks/useAppConfig";

type CategoryContextType = {
  getCategories: (
    showOutStock?: boolean,
    orderCategory?: string
  ) => Promise<CategoryType[]>;
  getCategoriesAll: (
    showOutStock?: boolean,
    orderCategory?: string
  ) => Promise<CategoryType[]>;
  getSubcategories: (
    categoryId: string,
    showOutStock?: boolean,
    orderCategory?: string
  ) => Promise<CategoryType[]>;
  getSubcategorySelected: (
    categoryId: string
  ) => Promise<CategoryType> | CategoryType | null;
  getCatalogs: () => Promise<CatalogType[]>;
  getChildrenCategories: (categoryId: string) => Promise<CategoryType[]>;
  subcategoryToAgroup: SimpleCategoryType | null;
  setSubcategoryToAgroup: (subcategory: SimpleCategoryType | null) => void;
};

type CategoryContextProviderProps = {
  children: ReactNode;
};

export type CategoryType = {
  id: number;
  name: string;
  img: string;
  code: string;
  parent: string;
  variations: number;
  children?: CategoryType[];
  order: number;
  initialHour?: string;
  finalHour?: string;
  status: boolean;
};

export type SimpleCategoryType = {
  id: number;
  name: string;
  img: string;
  code: string;
  parent: string;
  initialHour?: string;
  finalHour?: string;
  order: number;
};

export type CatalogType = {
  id: number;
  catalog: string;
  imgCatalog: string;
  sellerId?: string;
  isExclusive: boolean;
  sellerCatalog: boolean;
};

export const CategoryContext = createContext({} as CategoryContextType);

export function CategoryContextProvider(props: CategoryContextProviderProps) {
  const { getToken, getThisPluginspace, getLocations, subDomain } =
    usePluginspace();
  const { pluginspace_id } = useParams();
  const [categories, setCategories] = useState<CategoryType[] | null>(null);
  const [categoriesAll, setCategoriesAll] = useState<CategoryType[] | null>(
    null
  );
  const [subcategories, setSubcategories] = useState<CategoryType[] | null>(
    null
  );
  const [catalogs, setCatalogs] = useState<Array<CatalogType> | null>(null);
  const [subcategoryToAgroup, setSubcategoryToAgroup] =
    useState<SimpleCategoryType | null>(null);
  const { getAppConfig } = useAppConfig();
  const location = useLocation();
  useEffect(() => {
    loadAllCategories();
  }, [pluginspace_id, subDomain]);

  async function loadAllCategories() {
    const shouldLoadCategories =
      location.pathname !== `/${subDomain}/queue` && subDomain;

    if (!categoriesAll && shouldLoadCategories) {
      await getCategoriesAll();
    }
  }

  function verifyChildrenCategories(childrenCategories: CategoryType[]) {
    let childrenValids = false;
    childrenCategories.forEach((child) => {
      if (child.variations > 0 && child.children && child.children.length === 0)
        childrenValids = true;
    });
    if (childrenValids) return true;
    else {
      childrenCategories.forEach((child) => {
        if (child.children) {
          if (!childrenValids)
            childrenValids = verifyChildrenCategories(child.children);
        }
      });
      return childrenValids;
    }
  }

  function verifyValidSubcategory(category: CategoryType) {
    if (category.children && category.children.length > 0) {
      let validChildren = verifyChildrenCategories(category.children);
      return validChildren;
    } else if (category.variations > 0) {
      return true;
    } else return false;
  }

  function filterValidCategories(categories: CategoryType[]) {
    const filteredCategories: CategoryType[] = [];
    categories.forEach((category: CategoryType) => {
      if (verifyValidSubcategory(category)) filteredCategories.push(category);
    });
    return filteredCategories;
  }

  async function getCategories(showOutStock?: boolean, orderCategory?: string) {
    if (categories) return categories;
    else {
      const locations = await getLocations();

      const location = locations.find(
        (el) => el.code === "withdraw" && el.status === true
      );

      const conf = await getAppConfig();

      if (showOutStock === undefined) {
        showOutStock =
          conf &&
          conf.content &&
          conf.content.pdv &&
          conf.content.pdv.showOutStock
            ? conf.content.pdv.showOutStock
            : false;
      }
      if (orderCategory === undefined) {
        orderCategory =
          conf && conf.content && conf.content.orderCategory
            ? conf.content.orderCategory
            : "";
      }
      return await api
        .get(
          `/api/classification/product-count?noStock=${
            showOutStock ? true : false
          }`,
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then(async (response) => {
          const categoriesWithImageAjusted = response.data.data.map(
            (category: CategoryType) => {
              if (!category.img.includes("http")) {
                category.img = `https://s3.amazonaws.com/img.ninegrid.com/${category.img}`;
              }
              return category;
            }
          );
          const formatedCategories = categoriesWithImageAjusted
            .map((cat: CategoryType) => {
              const child: CategoryType[] = [];
              categoriesWithImageAjusted.forEach((otherCat: CategoryType) => {
                if (otherCat.parent === cat.code) {
                  child.push(otherCat);
                }
              });
              cat.children = child;
              return cat;
            })
            .filter(
              (category: CategoryType) =>
                category.parent === "" || category.parent === null
            );
          const categoriesFather = filterValidCategories(formatedCategories);
          if (orderCategory && orderCategory === "order") {
            const orderedCategory = categoriesFather
              .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
              .sort((a, b) =>
                a.order > b.order ? 1 : b.order > a.order ? -1 : 0
              );
            setCategories(orderedCategory);
            return orderedCategory;
          } else {
            const orderedCategory = categoriesFather.sort((a, b) =>
              a.name > b.name ? 1 : b.name > a.name ? -1 : 0
            );
            setCategories(orderedCategory);
            return orderedCategory;
          }
        })
        .catch((err) => {
          console.log(err);
          return [];
        });
    }
  }

  async function getCategoriesAll(
    showOutStock?: boolean,
    orderCategory?: string
  ) {
    if (categoriesAll) return categoriesAll;
    else {

      const conf = await getAppConfig();

      if (showOutStock === undefined) {
        showOutStock =
          conf &&
          conf.content &&
          conf.content.pdv &&
          conf.content.pdv.showOutStock
            ? conf.content.pdv.showOutStock
            : false;
      }
      if (orderCategory === undefined) {
        orderCategory =
          conf && conf.content && conf.content.orderCategory
            ? conf.content.orderCategory
            : "";
      }

      return await api
        .get(
          `/api/classification/product-count?noStock=${
            showOutStock ? true : false
          }`,
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then(async (response) => {
          const categoriesWithImageAjusted = response.data.data.map(
            (category: CategoryType) => {
              if (!category.img.includes("http")) {
                category.img = `https://s3.amazonaws.com/img.ninegrid.com/${category.img}`;
              }
              return category;
            }
          );
          const formatedCategories = categoriesWithImageAjusted.map(
            (cat: CategoryType) => {
              const child: CategoryType[] = [];
              categoriesWithImageAjusted.forEach((otherCat: CategoryType) => {
                if (otherCat.parent === cat.code) {
                  child.push(otherCat);
                }
              });
              cat.children = child;
              return cat;
            }
          );
          const categoriesFather = filterValidCategories(formatedCategories);
          if (orderCategory && orderCategory === "order") {
            const orderedCategory = categoriesFather
              .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
              .sort((a, b) =>
                a.order > b.order ? 1 : b.order > a.order ? -1 : 0
              );
            setCategoriesAll(orderedCategory);
            return orderedCategory;
          } else {
            const orderedCategory = categoriesFather.sort((a, b) =>
              a.name > b.name ? 1 : b.name > a.name ? -1 : 0
            );
            setCategoriesAll(orderedCategory);
            return orderedCategory;
          }
        })
        .catch((err) => {
          console.log(err);
          return [];
        });
    }
  }

  async function getCatalogs() {
    if (catalogs) return catalogs;
    else {
      return await api
        .get("/api/catalog/active", {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((responseCatalog) => {
          const catalogsWithImageAjusted = responseCatalog.data.data.map(
            (catalog: CatalogType) => {
              if (catalog.imgCatalog && !catalog.imgCatalog.includes("http")) {
                catalog.imgCatalog = `https://s3.amazonaws.com/img.ninegrid.com/${catalog.imgCatalog}`;
              }
              return catalog;
            }
          );
          setCatalogs(catalogsWithImageAjusted);
          return catalogsWithImageAjusted;
        })
        .catch((err) => {
          return [];
        });
    }
  }

  async function getSubcategories(
    categoryId: string,
    showOutStock?: boolean,
    orderCategory?: string
  ) {
    try {
      let subcategoriesToWork = await getCategoriesAll(showOutStock);
      let categorySelected = subcategoriesToWork.find(
        (category) => category.id.toString() === categoryId
      );

      const locations = await getLocations();

      const location = locations.find(
        (el) => el.code === "withdraw" && el.status === true
      );

      const conf = await getAppConfig();

      if (showOutStock === undefined) {
        showOutStock =
          conf &&
          conf.content &&
          conf.content.pdv &&
          conf.content.pdv.showOutStock
            ? conf.content.pdv.showOutStock
            : false;
      }
      if (orderCategory === undefined) {
        orderCategory =
          conf && conf.content && conf.content.orderCategory
            ? conf.content.orderCategory
            : "";
      }

      if (!subcategoriesToWork || subcategoriesToWork.length <= 0) {
        subcategoriesToWork = await api
          .get(
            `/api/classification/product-count?noStock=${
              showOutStock ? true : false
            }`,
            {
              headers: {
                Authorization: `Bearer ${await getToken()}`,
              },
            }
          )
          .then(async (responseFilter) => {
            setSubcategories(responseFilter.data.data);
            if (!categorySelected) {
              categorySelected = responseFilter.data.data.find(
                (category: CategoryType) =>
                  category.id.toString() === categoryId
              );
            }
            return responseFilter.data.data;
          })
          .catch((err) => {
            console.log(err);
            return [];
          });
      }
      const subcategoriesWithImageAjusted = subcategoriesToWork.map(
        (subcategory: CategoryType) => {
          if (!subcategory.img.includes("http")) {
            subcategory.img = `https://s3.amazonaws.com/img.ninegrid.com/${subcategory.img}`;
          }
          return subcategory;
        }
      );
      const formatedSubcategories = subcategoriesWithImageAjusted
        .map((sub: CategoryType) => {
          const child: CategoryType[] = [];
          subcategoriesWithImageAjusted.forEach((otherSub: CategoryType) => {
            if (otherSub.parent === sub.code) {
              child.push(otherSub);
            }
          });
          sub.children = child;
          return sub;
        })
        .filter(
          (subcategory: CategoryType) =>
            subcategory.parent ===
            (categorySelected ? categorySelected.code : "")
        )
        .filter((subcategory: CategoryType) => {
          if (subcategory.initialHour && subcategory.finalHour) {
            const nowDate = new Date();
            const initialDate = new Date(
              `${nowDate.toISOString().split("T")[0]} ${
                subcategory.initialHour
              }`
            );
            const finalDate = new Date(
              `${nowDate.toISOString().split("T")[0]} ${subcategory.finalHour}`
            );
            return (
              initialDate.getTime() <= nowDate.getTime() &&
              finalDate.getTime() >= nowDate.getTime()
            );
          } else {
            return true;
          }
        });
      const subcategoriesValid = filterValidCategories(formatedSubcategories);
      if (orderCategory && orderCategory === "order") {
        const orderedSubcategory = subcategoriesValid
          .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
          .sort((a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0));
        setSubcategories(orderedSubcategory);
        return orderedSubcategory;
      } else {
        const orderedSubcategory = subcategoriesValid.sort((a, b) =>
          a.name > b.name ? 1 : b.name > a.name ? -1 : 0
        );
        setSubcategories(orderedSubcategory);
        return orderedSubcategory;
      }
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  async function getSubcategorySelected(categoryId: string) {
    let subcategoryStorage = null;
    if (categoriesAll && categoriesAll.length) {
      subcategoryStorage = categoriesAll.find(
        (category) => category.id.toString() === categoryId
      );
    }
    if (subcategoryStorage) {
      return subcategoryStorage;
    } else {
      return await api
        .get(`api/classification/${categoryId}`, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then(async (response) => {
          if (response.data && response.data.data && response.data.data[0]) {
            return response.data.data[0];
          } else {
            return null;
          }
        })
        .catch((err) => {
          console.log(err);
          return null;
        });
    }
  }

  async function getChildrenCategories(categoryId: string) {
    const savedCategories = await getCategoriesAll();
    const thisCategory = savedCategories.find(
      (sub) => sub.id.toString() === categoryId
    );
    if (thisCategory) {
      return savedCategories.filter(
        (sub) =>
          sub.status &&
          (sub.code === thisCategory.code || sub.parent === thisCategory.code)
      );
    }
    return [];
  }

  return (
    <CategoryContext.Provider
      value={{
        getCategories,
        getCategoriesAll,
        getSubcategories,
        getCatalogs,
        getSubcategorySelected,
        getChildrenCategories,
        subcategoryToAgroup,
        setSubcategoryToAgroup,
      }}
    >
      {props.children}
    </CategoryContext.Provider>
  );
}
